/**
 * Session handler – 401 handler, inactivity-based "Are you still there?" modal, and silent token refresh.
 * Modal shows after a period of inactivity (security); when user is active and token is near expiry we refresh silently.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@contexts/AuthContext';
import { setAuthFailureHandler, setRefreshInProgress } from '@utils/authFailureHandler';
import {
  setPendingLogoutReason,
  type LogoutReason,
} from '@utils/logoutReasonStorage';
import { Modal } from '@components/common/Modal';
import { Button } from '@components/common/Button';
import { EVENTS } from '@constants/telemetryEvents';
import { useTelemetry } from '@hooks/useTelemetry';
import {
  IDLE_COUNTDOWN_SECONDS,
  inactivityWarningMs,
} from '@utils/sessionIdleTimeout';

/** When token has less than this until expiry and user is active, refresh silently. */
const TOKEN_REFRESH_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
/** How often to check if we should silently refresh (when active, token near expiry). */
const SILENT_REFRESH_CHECK_MS = 60 * 1000; // 1 minute
/** Throttle activity updates so we don't flood. */
const ACTIVITY_THROTTLE_MS = 2000; // 2 seconds
const COUNTDOWN_SECONDS = IDLE_COUNTDOWN_SECONDS;

function getTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: number };
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SessionHandler() {
  const navigate = useNavigate();
  const { token, user, clearSession, refreshSession } = useAuth();
  const { track } = useTelemetry();
  const warningMs = inactivityWarningMs(user?.role);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  // Set to "now" by the activity effect whenever a session starts; nothing reads it before then.
  const lastActivityRef = useRef(0);
  const activityThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshingRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  const redirectToLogin = useCallback(
    (logoutReason: LogoutReason) => {
      setPendingLogoutReason(logoutReason);
      clearSession();
      navigate('/login', {
        state: { logoutReason },
        replace: true,
      });
    },
    [clearSession, navigate]
  );

  const forceLogout = useCallback(() => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;
    setShowWarning(false);
    track(EVENTS.SESSION_IDLE_TIMEOUT, { role: user?.role ?? 'unknown' });
    redirectToLogin('session_expired');
  }, [redirectToLogin, track, user?.role]);

  // Register 401 handler so API layer can trigger logout + redirect
  useEffect(() => {
    setAuthFailureHandler((reason) => {
      const resolved: LogoutReason =
        reason === 'session_revoked' ? 'session_revoked' : 'session_expired';
      redirectToLogin(resolved);
    });
    return () => setAuthFailureHandler(undefined);
  }, [redirectToLogin]);

  // When user is logged out (e.g. session expired in another tab or 401), force-close the modal
  // so they don't see a stale countdown when they return to the tab.
  const [prevToken, setPrevToken] = useState(token);
  if (token !== prevToken) {
    setPrevToken(token);
    if (!token) {
      setShowWarning(false);
      setCountdown(COUNTDOWN_SECONDS);
    }
  }
  useEffect(() => {
    if (!token) logoutInProgressRef.current = false;
  }, [token]);

  // Track user activity (throttled) and schedule inactivity warning
  useEffect(() => {
    if (!token) return;

    function onActivity() {
      if (activityThrottleRef.current) return;
      activityThrottleRef.current = setTimeout(() => {
        activityThrottleRef.current = null;
        lastActivityRef.current = Date.now();
        setShowWarning(false);
        setCountdown(COUNTDOWN_SECONDS);
      }, ACTIVITY_THROTTLE_MS);
    }

    lastActivityRef.current = Date.now();

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onActivity));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current);
        activityThrottleRef.current = null;
      }
    };
  }, [token]);

  // Silent refresh when user is active and token is near expiry (no modal)
  useEffect(() => {
    if (!token || showWarning) return;

    const refreshIfNearExpiry = async () => {
      const now = Date.now();
      const inactiveMs = now - lastActivityRef.current;
      if (inactiveMs >= warningMs) return; // inactive: don't refresh, let inactivity modal handle it
      const expMs = getTokenExpiryMs(token);
      if (!expMs || expMs - now >= TOKEN_REFRESH_BEFORE_MS) return; // token fine or not parseable
      if (refreshingRef.current) return;
      setRefreshInProgress(true);
      refreshingRef.current = true;
      try {
        await refreshSession();
      } catch {
        // ignore; 401 or next check will handle
      } finally {
        setRefreshInProgress(false);
        refreshingRef.current = false;
      }
    };
    const intervalId = setInterval(() => void refreshIfNearExpiry(), SILENT_REFRESH_CHECK_MS);

    return () => clearInterval(intervalId);
  }, [token, showWarning, refreshSession, warningMs]);

  // Inactivity warning + countdown derived from absolute elapsed time since last activity.
  // This prevents drift/stall while tab/browser/computer is backgrounded.
  useEffect(() => {
    if (!token) return;

    const warningStartMs = warningMs;
    const logoutAfterWarningMs = COUNTDOWN_SECONDS * 1000;

    const syncWarningState = () => {
      const now = Date.now();
      const inactiveMs = now - lastActivityRef.current;

      if (inactiveMs < warningStartMs) {
        setShowWarning(false);
        setCountdown(COUNTDOWN_SECONDS);
        return;
      }

      const remainingMs = warningStartMs + logoutAfterWarningMs - inactiveMs;
      const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));

      if (remainingSeconds <= 0) {
        forceLogout();
        return;
      }

      setShowWarning(true);
      setCountdown(remainingSeconds);
    };

    syncWarningState();
    const intervalId = setInterval(syncWarningState, 1000);
    window.addEventListener('focus', syncWarningState);
    window.addEventListener('pageshow', syncWarningState);
    document.addEventListener('visibilitychange', syncWarningState);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', syncWarningState);
      window.removeEventListener('pageshow', syncWarningState);
      document.removeEventListener('visibilitychange', syncWarningState);
    };
  }, [token, forceLogout, warningMs]);

  /** "Stay logged in" = full inactivity reset and a fresh access token. */
  const handleStayLoggedIn = useCallback(async () => {
    if (activityThrottleRef.current) {
      clearTimeout(activityThrottleRef.current);
      activityThrottleRef.current = null;
    }
    lastActivityRef.current = Date.now();
    logoutInProgressRef.current = false;
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECONDS);
    setRefreshInProgress(true);
    try {
      await refreshSession();
      lastActivityRef.current = Date.now();
    } catch {
      // Next API call or inactivity check will handle a dead session
    } finally {
      setRefreshInProgress(false);
    }
  }, [refreshSession]);

  const handleLogOut = useCallback(() => {
    forceLogout();
  }, [forceLogout]);

  return (
    <Modal
      isOpen={showWarning}
      onClose={handleStayLoggedIn}
      title="Are you still there?"
      description="You've been inactive. Choose to stay logged in or log out to end your session."
      maxWidth="400px"
      hideCloseButton
      closeOnBackdropClick={false}
      closeOnEscape={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-gray-700)' }}>
          You will be logged out in <strong>{formatCountdown(countdown)}</strong> due
          to inactivity. Log out now or stay logged in to continue.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 8,
          }}
        >
          <Button variant="ghost" onClick={handleLogOut}>
            Log out
          </Button>
          <Button variant="primary" onClick={handleStayLoggedIn}>
            Stay logged in
          </Button>
        </div>
      </div>
    </Modal>
  );
}
